import * as Y from "yjs";
import { allWidgets, type SqlWidgetDef } from "@playbookai/ui";
import { metaMap, widgetsMap } from "@playbookai/collab-client";
import { SOLVE_DEFAULT_PHASE } from "../widgets/phase-mapping.js";
import {
  canonicalWidgetSlot,
  type WidgetLayoutEntry,
} from "./phase-metadata.js";

export const SOLVE_WIDGET_LAYOUT_VERSION = 6;

const LEGACY_OPPRESS_V4_LAYOUT_ENTRIES: readonly WidgetLayoutEntry[] = [
  {
    phase: "Oppress",
    layout: { col: 0, row: 0, width: 352, height: 300 },
  },
  {
    phase: "Oppress",
    layout: { col: 1, row: 0, width: 352, height: 300 },
  },
  {
    phase: "Oppress",
    layout: { col: "full", row: 1, width: 752, height: 360 },
  },
  {
    phase: "Oppress",
    layout: { col: "full", row: 2, width: 752, height: 360 },
  },
];

const LEGACY_OPPRESS_V4_LAYOUT = {
  problemTimeline: LEGACY_OPPRESS_V4_LAYOUT_ENTRIES[2]!.layout,
  containmentPlan: LEGACY_OPPRESS_V4_LAYOUT_ENTRIES[3]!.layout,
} as const;

interface WidgetGeometry {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** Specify kinds whose canonical slot moved between version 5 and version 6. */
const SPECIFY_V6_SHIFTED_KINDS = ["tags", "runChart", "problemTree"] as const;

/**
 * Frozen version 5 Specify geometry. Version 6 grew Tags from 300 to 420, and
 * because a canonical row is as tall as its tallest widget that pushes every
 * later Specify row down by 120px. Problem Statement shares row 0 with Tags at
 * the same height it always had, so it is absent here — it never moves.
 */
const LEGACY_SPECIFY_V5_GEOMETRY: Readonly<
  Record<(typeof SPECIFY_V6_SHIFTED_KINDS)[number], WidgetGeometry>
> = {
  tags: { x: 432, y: 96, w: 352, h: 324 },
  runChart: { x: 32, y: 444, w: 352, h: 324 },
  problemTree: { x: 32, y: 792, w: 752, h: 584 },
};

/**
 * Heights a version 2 or 3 canvas can hold for a kind whose canonical height
 * later changed — 300 before the drag bar, 324 after it. Tags is the only
 * entry: version 6 grew it to 420/444, so its live `layout.height` matches
 * nothing a legacy canvas ever stored and the generic rule below would skip it.
 */
const LEGACY_V3_HEIGHTS: Readonly<Record<string, readonly number[]>> = {
  tags: [300, 324],
};

/**
 * One-time layout migration for SOLVE default-roster widgets.
 *
 * Existing canvases may already have `meta.seeded=true` and overlapping
 * positions from an older seed pass. This function resets only the canonical
 * default instance of every default-roster kind to the reference slot and
 * leaves additional multi-instance widgets untouched.
 */
export async function ensureSolveWidgetLayout(
  doc: Y.Doc,
  ..._legacyArguments: readonly unknown[]
): Promise<void> {
  const meta = metaMap(doc);
  const current = meta.get("solveWidgetLayoutVersion");
  if (typeof current === "number" && current >= SOLVE_WIDGET_LAYOUT_VERSION)
    return;
  const widgetHeightOnly = current === 2 || current === 3;
  const oppressFullLaneOnly = current === 4;
  const specifyRowShiftOnly = current === 5;
  // Every supported legacy version predates the taller Tags widget, so each
  // version-specific pass below is followed by the Specify row reconciliation.
  // Only an unversioned canvas takes the full canonical reset instead.
  const legacyFenced =
    widgetHeightOnly || oppressFullLaneOnly || specifyRowShiftOnly;

  const sqlDefs = allWidgets().filter(isSqlWidget);
  const defaultDefs = sqlDefs.filter(
    (def) =>
      def.defaultRoster && SOLVE_DEFAULT_PHASE[def.kind] && !legacyFenced,
  );
  const layoutEntries = sqlDefs.flatMap((def): WidgetLayoutEntry[] => {
    const phase = SOLVE_DEFAULT_PHASE[def.kind];
    return phase ? [{ phase, layout: def.layout }] : [];
  });

  const refsByKind = groupRefsByKind(doc);
  doc.transact(() => {
    if (widgetHeightOnly) {
      const defsByKind = new Map(sqlDefs.map((def) => [def.kind, def]));
      widgetsMap(doc).forEach((ymap) => {
        const kind = ymap.get("kind");
        if (typeof kind !== "string") return;
        const def = defsByKind.get(kind as SqlWidgetDef<unknown>["kind"]);
        if (!def || def.defaultSize.h !== def.layout.height + 24) return;
        const height = ymap.get("h");
        if (typeof height !== "number") return;
        if (
          height !== def.defaultSize.h &&
          !legacyHeightsFor(def).includes(height)
        )
          return;
        if (height !== def.defaultSize.h) ymap.set("h", def.defaultSize.h);
      });
    }

    if (oppressFullLaneOnly) {
      // Exposure Review was not part of v4; any existing instance is
      // user-authored or experimental and must remain exactly where it is.
      for (const kind of ["problemTimeline", "containmentPlan"] as const) {
        const def = sqlDefs.find((candidate) => candidate.kind === kind);
        if (!def) continue;
        const phase = SOLVE_DEFAULT_PHASE[kind];
        if (!phase) continue;
        const refs = refsByKind.get(kind) ?? [];
        const ymap = chooseMarkedDefaultRef(def, refs);
        if (!ymap) continue;
        // Version 4 predates the current Oppress roster. Use the frozen
        // historical slots so fresh-roster metadata cannot move old canvases.
        const canonical = canonicalWidgetSlot(
          phase,
          LEGACY_OPPRESS_V4_LAYOUT[kind],
          LEGACY_OPPRESS_V4_LAYOUT_ENTRIES,
        );
        ymap.set("phase", phase);
        ymap.set("x", canonical.x);
        ymap.set("y", canonical.y);
        ymap.set("w", def.defaultSize.w);
        ymap.set("h", def.defaultSize.h);
      }
    }

    if (legacyFenced) {
      // Move only the canonical default instance, and only while it still sits
      // exactly on its version 5 slot — any other geometry is a user move or
      // resize and stays put. A version 2/3 canvas reaches those slots because
      // the height pass above has already normalized its widget heights.
      for (const kind of SPECIFY_V6_SHIFTED_KINDS) {
        const def = sqlDefs.find((candidate) => candidate.kind === kind);
        if (!def) continue;
        const phase = SOLVE_DEFAULT_PHASE[kind];
        if (!phase) continue;
        const ymap = chooseMarkedDefaultRef(def, refsByKind.get(kind) ?? []);
        if (!ymap) continue;
        const slot = canonicalWidgetSlot(phase, def.layout, layoutEntries);
        const target: WidgetGeometry = {
          x: slot.x,
          y: slot.y,
          w: def.defaultSize.w,
          h: def.defaultSize.h,
        };
        const onLegacySlot = matchesLegacyGeometry(
          ymap,
          LEGACY_SPECIFY_V5_GEOMETRY[kind],
          target,
        );
        if (!onLegacySlot && !matchesGeometry(ymap, target)) continue;
        if (onLegacySlot) {
          ymap.set("x", target.x);
          ymap.set("y", target.y);
          ymap.set("w", target.w);
          ymap.set("h", target.h);
        }
      }
      return;
    }

    for (const def of defaultDefs) {
      const phase = SOLVE_DEFAULT_PHASE[def.kind];
      if (!phase) continue;
      const ymap = chooseDefaultRef(def, refsByKind.get(def.kind) ?? []);
      if (!ymap) continue;

      const slot = canonicalWidgetSlot(phase, def.layout, layoutEntries);
      ymap.set("phase", phase);
      ymap.set("isDefaultRoster", true);
      ymap.set("rosterKey", def.kind);
      ymap.set("x", slot.x);
      ymap.set("y", slot.y);
      ymap.set("w", def.defaultSize.w);
      ymap.set("h", def.defaultSize.h);
    }
  });
  // Yjs is the sole canvas-write path. Backend placement projections are
  // derived from Collaboration Service updates (ADR-0004), never dual-written
  // by this client migration.
  doc.transact(() => {
    meta.set("solveWidgetLayoutVersion", SOLVE_WIDGET_LAYOUT_VERSION);
  });
}

export function markDefaultRosterRef(ymap: Y.Map<unknown>, kind: string): void {
  ymap.set("isDefaultRoster", true);
  ymap.set("rosterKey", kind);
}

function groupRefsByKind(doc: Y.Doc): Map<string, Y.Map<unknown>[]> {
  const grouped = new Map<string, Y.Map<unknown>[]>();
  widgetsMap(doc).forEach((ymap) => {
    const kind = ymap.get("kind");
    if (typeof kind !== "string") return;
    const refs = grouped.get(kind) ?? [];
    refs.push(ymap);
    grouped.set(kind, refs);
  });
  return grouped;
}

function chooseDefaultRef(
  def: SqlWidgetDef<unknown>,
  refs: ReadonlyArray<Y.Map<unknown>>,
): Y.Map<unknown> | null {
  if (refs.length === 0) return null;
  const marked = chooseMarkedDefaultRef(def, refs);
  if (marked) return marked;
  return [...refs].sort((a, b) => sortValue(a) - sortValue(b))[0] ?? null;
}

function chooseMarkedDefaultRef(
  def: SqlWidgetDef<unknown>,
  refs: ReadonlyArray<Y.Map<unknown>>,
): Y.Map<unknown> | null {
  return (
    refs.find(
      (ymap) =>
        ymap.get("isDefaultRoster") === true &&
        (ymap.get("rosterKey") === def.kind || ymap.get("kind") === def.kind),
    ) ?? null
  );
}

function legacyHeightsFor(def: SqlWidgetDef<unknown>): readonly number[] {
  return LEGACY_V3_HEIGHTS[def.kind] ?? [def.layout.height];
}

function matchesGeometry(
  ymap: Y.Map<unknown>,
  geometry: WidgetGeometry,
): boolean {
  return (
    ymap.get("x") === geometry.x &&
    ymap.get("y") === geometry.y &&
    ymap.get("w") === geometry.w &&
    ymap.get("h") === geometry.h
  );
}

function matchesLegacyGeometry(
  ymap: Y.Map<unknown>,
  legacy: WidgetGeometry,
  normalized: WidgetGeometry,
): boolean {
  return (
    ymap.get("x") === legacy.x &&
    ymap.get("y") === legacy.y &&
    ymap.get("w") === legacy.w &&
    (ymap.get("h") === legacy.h || ymap.get("h") === normalized.h)
  );
}

function sortValue(ymap: Y.Map<unknown>): number {
  const createdAt = ymap.get("createdAt");
  if (typeof createdAt === "number" && Number.isFinite(createdAt))
    return createdAt;
  const z = ymap.get("z");
  if (typeof z === "number" && Number.isFinite(z)) return z;
  return 0;
}

function isSqlWidget(
  def: ReturnType<typeof allWidgets>[number],
): def is SqlWidgetDef<unknown> {
  return def.storage === "sql";
}
